import React, { useState } from 'react';
import CustomModal from 'src/containers/Reimbursements/common/CustomModal';
import { RISK_BUTTON_COLOR } from 'src/utils';
import { capitalizeEachWord } from 'src/utils/common';
import {
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Placeholder } from 'rsuite';
import { fetchRiskSubDetails } from 'src/redux/action/ewsService';

const EWS = ({
  open,
  setOpen,
  ewsRiskLevel,
  riskLevelDetails,
  claimId,
  subClaimId,
  totalRules,
  highRiskRules
}) => {
  const [loading, setLoading] = useState(false);
  const [subRiskDetails, setSubRiskDetails] = useState(null);
  const [expanded, setExpanded] = useState('');

  const getRiskDetails = async (id) => {
    if (!claimId && !subClaimId) return;
    setLoading(true);
    const response = await fetchRiskSubDetails(id);
    setSubRiskDetails(response);
    setLoading(false);
  };

  const handleChange = (panel, id) => (event, isExpanded) => {
    getRiskDetails(id);
    setExpanded(isExpanded ? panel : false);
  };

  const ENTITY_RENDER_CONFIG = {
    PINCODE: {
      title: 'Pincode',
      render: (item) => (
        <div style={{ display: 'flex', gap: 2 }}>
          {item?.pincode && <div>Pin Code: {item?.pincode}, </div>}
          {item?.pincodeState && item?.pincodeCity && (
            <div>
              {capitalizeEachWord(item?.pincodeCity)},{' '}
              {capitalizeEachWord(item?.pincodeState)}
            </div>
          )}
        </div>
      )
    },

    HOSPITAL: {
      title: 'Hospital',
      render: (item) => (
        <div style={{ display: 'flex', gap: 2 }}>
          {item.hospitalName && (
            <div>Hospital Name : {capitalizeEachWord(item?.hospitalName)},</div>
          )}
          {item?.hospitalCity && item?.hospitalState && (
            <div>
              {capitalizeEachWord(item?.hospitalCity)},{' '}
              {capitalizeEachWord(item?.hospitalState)}
            </div>
          )}
        </div>
      )
    }
  };

  return (
    <CustomModal
      open={open}
      setOpen={() => setOpen(false)}
      padding="5px 10px"
      minWidth={{
        xs: 'initial',
        sm: '60%'
      }}
      maxWidth={{
        xs: 'initial',
        sm: '60%',
        md: '70%'
      }}
      borderRadius="16px"
      title={'Risk Assessment Details'}
      children={
        <>
          <div
            style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              marginBottom: '15px'
            }}
          >
            <p>Overall Claim Status </p>
            <span
              style={{
                border: `1px solid ${RISK_BUTTON_COLOR[ewsRiskLevel]?.color}`,
                paddingLeft: '20px',
                paddingRight: '20px',
                paddingTop: '4px',
                paddingBottom: '4px',
                color: '#fff',
                fontWeight: '500',
                fontSize: '12px',
                borderRadius: '8px',
                backgroundColor: RISK_BUTTON_COLOR[ewsRiskLevel]?.color
              }}
            >
              {capitalizeEachWord(ewsRiskLevel) || 'N/A'}
            </span>
            {highRiskRules && totalRules && (
              <span>
                ({highRiskRules}/{totalRules})
              </span>
            )}
          </div>

          {riskLevelDetails?.length > 0 ? (
            riskLevelDetails?.map((item, index) => (
              <div style={{ marginBottom: '5px', padding: 1 }}>
                <Accordion
                  expanded={expanded === index}
                  onChange={handleChange(index, item?.reviewId)}
                >
                  <AccordionSummary expandIcon={<ExpandMore />} id={index}>
                    <div
                      style={{
                        width: '100%'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          minWidth: '250px',
                          textTransform: 'capitalize'
                        }}
                      >
                        <p>{capitalizeEachWord(item?.entityType)} Rule</p>
                        <span
                          style={{
                            border: `1px solid ${RISK_BUTTON_COLOR[ewsRiskLevel]?.color}`,
                            paddingLeft: '15px',
                            paddingRight: '15px',
                            paddingTop: '2px',
                            paddingBottom: '2px',
                            color: '#fff',
                            fontWeight: '500',
                            fontSize: '12px',
                            borderRadius: '8px',
                            backgroundColor:
                              RISK_BUTTON_COLOR[ewsRiskLevel]?.color
                          }}
                        >
                          {capitalizeEachWord(item?.riskLevel)}
                        </span>
                        {item.highRiskRules && item.totalRules && (
                          <span>
                            ({item.highRiskRules}/{item.totalRules})
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          textAlign: 'right',
                          fontSize: '12px',
                          color: '#555',
                          width: '100%'
                        }}
                      >
                        {ENTITY_RENDER_CONFIG[item?.entityType]?.render(item)}
                      </div>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer component={Paper}>
                      <Table
                        sx={{
                          '& th, & td': {
                            border: '1px solid #e0e0e0'
                          }
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell align="center">
                              <b>Sub Category</b>
                            </TableCell>
                            <TableCell align="center">
                              <b>Risk Level</b>
                            </TableCell>
                            <TableCell align="center">
                              <b>Description</b>
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {loading ? (
                            <TableRow>
                              <TableCell colSpan={4}>
                                <Placeholder.Paragraph
                                  active
                                  style={{ minWidth: 600 }}
                                />
                              </TableCell>
                            </TableRow>
                          ) : (
                            subRiskDetails?.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell
                                  align="center"
                                  sx={{ maxWidth: 150 }}
                                >
                                  {row.subCategoryName}
                                </TableCell>

                                <TableCell align="center">
                                  <Chip
                                    label={capitalizeEachWord(row?.riskLevel)}
                                    style={{
                                      border: `1px solid ${
                                        RISK_BUTTON_COLOR[row?.riskLevel]?.color
                                      }`,
                                      paddingLeft: '20px',
                                      paddingRight: '20px',
                                      paddingTop: '4px',
                                      paddingBottom: '4px',
                                      color: '#fff',
                                      fontWeight: '500',
                                      fontSize: '12px',
                                      borderRadius: '8px',
                                      backgroundColor:
                                        RISK_BUTTON_COLOR[row?.riskLevel]?.color
                                    }}
                                    size="small"
                                  />
                                </TableCell>

                                <TableCell
                                  align="center"
                                  sx={{
                                    maxWidth: 500,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.6
                                  }}
                                >
                                  {row?.description ? (
                                    <ul
                                      style={{
                                        listStyleType: 'disc',
                                        paddingLeft: '20px',
                                        margin: 0,
                                        textAlign: 'left'
                                      }}
                                    >
                                      {row.description
                                        .split('$')
                                        .filter((item) => item.trim() !== '')
                                        .map((item, index) => (
                                          <li
                                            key={index}
                                            style={{
                                              display: 'list-item',
                                              listStylePosition: 'outside'
                                            }}
                                          >
                                            {item.trim()}
                                          </li>
                                        ))}
                                    </ul>
                                  ) : (
                                    '---'
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', margin: '20px' }}>
              No data found
            </div>
          )}
        </>
      }
    />
  );
};

export default EWS;
